import React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface PortfolioCreatorProps {
  newPortfolioName: string;
  setNewPortfolioName: (name: string) => void;
  portfolioType: string;
  setPortfolioType: (type: string) => void;
  onBack: () => void;
  isLoading: boolean;
  isTypeOpen: boolean;
  setIsTypeOpen: (isOpen: boolean) => void;
  // When true, hide the "Model" type option (used for build flows that must target personal/client only)
  hideModelType?: boolean;
}

export default function PortfolioCreator({
  newPortfolioName,
  setNewPortfolioName,
  portfolioType,
  setPortfolioType,
  onBack,
  isLoading,
  isTypeOpen,
  setIsTypeOpen,
  hideModelType,
}: PortfolioCreatorProps) {
  return (
    <div className="grid gap-2">
      <Label htmlFor="new-portfolio">New Portfolio Name</Label>
      <Input
        id="new-portfolio"
        value={newPortfolioName}
        onChange={(e) => setNewPortfolioName(e.target.value)}
        placeholder="Enter portfolio name"
        disabled={isLoading}
      />
      
      <div className="mt-4">
        <Label htmlFor="portfolio-type">Portfolio Type</Label>
        <Popover open={isTypeOpen} onOpenChange={setIsTypeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={isTypeOpen}
              className="w-full justify-between mt-1"
              disabled={isLoading}
            >
              {portfolioType === "personal" || portfolioType === "" ? "Personal" :
               portfolioType === "model" ? "Model" : "Client"}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandList>
                <CommandGroup>
                  <CommandItem
                    value="personal"
                    onSelect={() => {
                      setPortfolioType("personal");
                      setIsTypeOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        portfolioType === "personal" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    Personal
                  </CommandItem>
                  {!hideModelType && (
                    <CommandItem
                      value="model"
                      onSelect={() => {
                        setPortfolioType("model");
                        setIsTypeOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          portfolioType === "model" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      Model
                    </CommandItem>
                  )}
                  <CommandItem
                    value="client"
                    onSelect={() => {
                      setPortfolioType("client");
                      setIsTypeOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        portfolioType === "client" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    Client
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      
      <Button 
        variant="link" 
        className="mt-2 justify-start px-0"
        onClick={onBack}
      >
        &larr; Select existing portfolio
      </Button>
    </div>
  );
}
